import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Star, Users, ArrowRight } from "lucide-react";
import { api } from "@/api";
import Reveal, { usePageMeta } from "@/components/Reveal";

export default function Properties() {
  usePageMeta("Nos logements", "Découvrez les logements en location courte durée gérés par Golden Aura Pro et réservez directement sur Airbnb.");
  const [properties, setProperties] = useState(null);

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => setProperties([]));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="properties-page">
      <Reveal className="text-center mb-20">
        <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-5">Nos logements</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl text-[#F6F1E6] mb-6">
          Une collection <span className="italic gold-text">confidentielle</span>
        </h1>
        <p className="text-base md:text-lg text-[#9AA3B5] max-w-2xl mx-auto leading-relaxed">
          Deux résidences soigneusement sélectionnées et gérées avec exigence. Réservation sécurisée directement sur Airbnb — aucun paiement n'est effectué sur ce site.
        </p>
      </Reveal>

      {properties === null ? (
        <p className="text-center text-[#9AA3B5]" data-testid="properties-loading">Chargement des logements…</p>
      ) : (
        <div className="space-y-24">
          {properties.map((p, i) => (
            <Reveal key={p.key} delay={0.05}>
              <article
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center ${i % 2 === 1 ? "" : ""}`}
                data-testid={`property-row-${p.key}`}
              >
                <Link
                  to={`/logements/${p.key}`}
                  className={`lg:col-span-7 group relative rounded-3xl overflow-hidden gold-frame h-[320px] sm:h-[440px] block ${i % 2 === 1 ? "lg:order-2" : ""}`}
                  data-testid={`property-row-image-${p.key}`}
                >
                  <img
                    src={p.images?.[0]}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-108 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E]/70 via-transparent to-transparent" />
                  {p.rating && (
                    <span className="absolute top-5 left-5 flex items-center gap-1.5 rounded-full bg-[#0A0F1E]/80 backdrop-blur px-4 py-2 text-xs text-[#E5C158] border border-[#D4AF37]/30">
                      <Star size={12} fill="currentColor" /> {p.rating}
                    </span>
                  )}
                </Link>

                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <p className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-4">
                    <MapPin size={14} /> {p.location}
                  </p>
                  <h2 className="font-display font-light text-3xl sm:text-4xl text-[#F6F1E6] mb-4">{p.name}</h2>
                  <p className="flex items-center gap-2 text-sm text-[#9AA3B5] mb-5">
                    <Users size={15} className="text-[#D4AF37]" /> {p.capacity}
                  </p>
                  <p className="text-base text-[#B9BFD0] leading-relaxed mb-8">{p.description}</p>
                  <div className="flex flex-wrap gap-3">
                    {p.airbnb_url && (
                      <a href={p.airbnb_url} target="_blank" rel="noopener noreferrer" data-testid={`property-row-airbnb-${p.key}`} className="gold-button rounded-full px-7 py-3.5 text-[11px] font-medium tracking-[0.16em] uppercase">
                        Réserver sur Airbnb
                      </a>
                    )}
                    {p.booking_url && (
                      <a href={p.booking_url} target="_blank" rel="noopener noreferrer" data-testid={`property-row-booking-${p.key}`} className="outline-button rounded-full px-7 py-3.5 text-[11px] font-medium tracking-[0.16em] uppercase">
                        Réserver sur Booking.com
                      </a>
                    )}
                    <Link to={`/logements/${p.key}`} data-testid={`property-row-detail-${p.key}`} className="group flex items-center gap-2 px-2 py-3.5 text-[11px] font-medium tracking-[0.16em] uppercase text-[#E5C158]">
                      La fiche complète <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
